import { useEffect, useMemo, useState } from 'react'
import { api } from './api.js'
import { pages, visible } from './conditions.js'
import { navigate } from './App.jsx'
import Champ from './Champ.jsx'
import Creneaux from './Creneaux.jsx'

// Formulaire en étapes. Les questions masquées par une règle ne sont jamais
// obligatoires : elles disparaissent sans bloquer personne.
export default function Demarche({ slug }) {
  const [form, setForm] = useState(null)
  const [data, setData] = useState({})
  const [etape, setEtape] = useState(0)
  const [creneau, setCreneau] = useState(null)
  const [manque, setManque] = useState([])
  const [recu, setRecu] = useState(null)
  const [erreur, setErreur] = useState('')
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => { api.form(slug).then(setForm).catch((e) => setErreur(e.message)) }, [slug])

  const etapes = useMemo(() => {
    if (!form) return []
    const out = pages(form.fields).map((p) => ({ kind: 'page', titre: p.titre || p.title || 'Votre demande', page: p }))
    if (form.agenda) out.push({ kind: 'rdv', titre: 'Créneau' })
    out.push({ kind: 'recap', titre: 'Vérification' })
    return out
  }, [form])

  if (erreur) return <div className="wrap"><p className="erreur">{erreur}</p></div>
  if (!form) return <div className="wrap"><p>Chargement…</p></div>

  if (recu) return (
    <div className="wrap">
      <section className="recu">
        <h1>Demande enregistrée</h1>
        <p className="numero">{recu.number}</p>
        <p className="code">Code de suivi : <strong>{recu.track_code}</strong></p>
        <p>{recu.confirm}</p>
        <p className="note">Conservez ce numéro et ce code : ils suffisent à suivre votre dossier,
          sans créer de compte.</p>
        <div className="actions">
          <button className="btn pri" onClick={() => navigate(`/suivi?n=${recu.number}&c=${recu.track_code}`)}>
            Suivre ce dossier
          </button>
          <button className="btn" onClick={() => navigate('/')}>Retour aux démarches</button>
        </div>
      </section>
    </div>
  )

  const courante = etapes[etape]

  function suivant() {
    if (courante.kind === 'page') {
      const requis = courante.page.fields.filter(
        (f) => f.required && visible(f.condition, data) &&
          (data[f.varname] == null || data[f.varname] === '' ||
            (Array.isArray(data[f.varname]) && !data[f.varname].length)))
      if (requis.length) {
        setManque(requis.map((f) => f.varname))
        document.getElementById('champ-' + requis[0].varname)?.focus()
        return
      }
    }
    if (courante.kind === 'rdv' && !creneau) {
      setErreur('Choisissez un créneau pour continuer.')
      return
    }
    setManque([])
    setErreur('')
    setEtape(etape + 1)
  }

  async function envoyer() {
    setEnvoi(true)
    try {
      const res = await api.submit(slug, { data, booking: creneau })
      setRecu({ ...res })
    } catch (e) {
      setErreur(e.message)
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="wrap">
      <h1>{form.title}</h1>
      <p className="chapo">{form.description}</p>

      <ol className="etapes">
        {etapes.map((e, i) => (
          <li key={i} className={i === etape ? 'on' : i < etape ? 'faite' : ''}
              aria-current={i === etape ? 'step' : undefined}>
            <span>{i < etape ? '✓' : i + 1}</span>{e.titre}
          </li>
        ))}
      </ol>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {courante.kind === 'page' && (
        <div className="formulaire">
          {courante.page.fields.filter((f) => visible(f.condition, data)).map((f) => (
            <Champ key={f.varname || f.label} champ={f} valeur={data[f.varname]}
                   manquant={manque.includes(f.varname)}
                   onChange={(v) => setData({ ...data, [f.varname]: v })} />
          ))}
        </div>
      )}

      {courante.kind === 'rdv' && (
        <Creneaux agenda={form.agenda} choisi={creneau} onChoix={setCreneau} />
      )}

      {courante.kind === 'recap' && (
        <table className="recap">
          <caption>Vérifiez vos réponses avant l'envoi</caption>
          <tbody>
            {form.fields.filter((f) => f.varname && visible(f.condition, data)).map((f) => (
              <tr key={f.varname}>
                <th scope="row">{f.label}</th>
                <td>{Array.isArray(data[f.varname]) ? data[f.varname].join(', ')
                  : data[f.varname] || <em>non renseigné</em>}</td>
              </tr>
            ))}
            {creneau && (
              <tr><th scope="row">Rendez-vous</th><td>{creneau.day} à {creneau.time}</td></tr>
            )}
          </tbody>
        </table>
      )}

      <div className="actions">
        <button className="btn" onClick={() => etape === 0 ? navigate('/') : setEtape(etape - 1)}>
          {etape === 0 ? 'Annuler' : 'Étape précédente'}
        </button>
        {courante.kind === 'recap'
          ? <button className="btn pri" onClick={envoyer} disabled={envoi}>
              {envoi ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          : <button className="btn pri" onClick={suivant}>Continuer</button>}
      </div>
    </div>
  )
}
